import { ActivityType } from "../../../types";
import type { FormActivityUI } from "./utils";

/**
 * Section order used to flatten the grouped view back into `lesson.activities`.
 * The backend derives `LessonToActivity.order_by` from the array index, so the
 * array must stay grouped START → MIDDLE → CLOSING for the persisted order to
 * read naturally.
 */
export const ACTIVITY_SECTION_ORDER: readonly ActivityType[] = [
  ActivityType.START,
  ActivityType.MIDDLE,
  ActivityType.CLOSING,
];

export interface ReorderRow {
  /** Stable identifier for drag-and-drop; activities may not have an id yet. */
  key: string;
  activity: FormActivityUI;
}

export type ReorderGroups = Record<ActivityType, ReorderRow[]>;

export interface ReorderTarget {
  rowKey: string;
  toType: ActivityType;
  /** Final index inside the destination section (clamped). */
  toIndex: number;
}

const emptyGroups = (): ReorderGroups => ({
  [ActivityType.START]: [],
  [ActivityType.MIDDLE]: [],
  [ActivityType.CLOSING]: [],
});

export const buildReorderGroups = (
  activities: FormActivityUI[],
): ReorderGroups => {
  const groups = emptyGroups();
  activities.forEach((activity, index) => {
    const type = ACTIVITY_SECTION_ORDER.includes(activity.activityType)
      ? activity.activityType
      : ActivityType.START;
    groups[type].push({
      key: activity.id != null ? `activity-${activity.id}` : `index-${index}`,
      activity,
    });
  });
  return groups;
};

const findRow = (groups: ReorderGroups, rowKey: string) => {
  for (const type of ACTIVITY_SECTION_ORDER) {
    const index = groups[type].findIndex((row) => row.key === rowKey);
    if (index !== -1) return { type, index, row: groups[type][index] };
  }
  return null;
};

export const findRowSection = (
  groups: ReorderGroups,
  rowKey: string,
): ActivityType | null => findRow(groups, rowKey)?.type ?? null;

/**
 * Moves a row to `toIndex` of `toType`. Semantics match `arrayMove`: the row is
 * removed first, then inserted at the clamped target index, so a same-section
 * move behaves exactly like a plain sortable reorder.
 */
export const moveReorderRow = (
  groups: ReorderGroups,
  { rowKey, toType, toIndex }: ReorderTarget,
): ReorderGroups => {
  const source = findRow(groups, rowKey);
  if (!source) return groups;
  if (source.type === toType && source.index === toIndex) return groups;

  const next: ReorderGroups = {
    [ActivityType.START]: [...groups[ActivityType.START]],
    [ActivityType.MIDDLE]: [...groups[ActivityType.MIDDLE]],
    [ActivityType.CLOSING]: [...groups[ActivityType.CLOSING]],
  };
  next[source.type].splice(source.index, 1);

  const movedRow: ReorderRow =
    source.type === toType
      ? source.row
      : {
          ...source.row,
          activity: { ...source.row.activity, activityType: toType },
        };

  const insertAt = Math.max(0, Math.min(toIndex, next[toType].length));
  next[toType].splice(insertAt, 0, movedRow);
  return next;
};

export const flattenReorderGroups = (
  groups: ReorderGroups,
): FormActivityUI[] =>
  ACTIVITY_SECTION_ORDER.flatMap((type) =>
    groups[type].map((row) =>
      row.activity.activityType === type
        ? row.activity
        : { ...row.activity, activityType: type },
    ),
  );

/**
 * Predicate that recognises `target` inside a possibly reordered array. Async
 * handlers capture an index before awaiting, and the array can change while the
 * request is in flight — matching by id keeps the result on the right activity.
 * Falls back to the captured index for an activity that has no id yet.
 */
export const matchActivity =
  (target: FormActivityUI, capturedIndex: number) =>
  (candidate: FormActivityUI, index: number): boolean =>
    target.id != null ? candidate.id === target.id : index === capturedIndex;

/** Present index of `target`, or -1 once it has been removed. */
export const findActivityIndex = (
  activities: FormActivityUI[],
  target: FormActivityUI,
  capturedIndex: number,
): number =>
  target.id == null
    ? capturedIndex
    : activities.findIndex((activity) => activity.id === target.id);

/**
 * `pillSuggestions` and `pillLoading` are keyed by the activity's index in
 * `lesson.activities`, so any reorder has to carry those entries to their new
 * index — otherwise a saved lesson would apply one activity's pedagogical pills
 * to another.
 */
export const remapActivityIndexedRecord = <T>(
  record: Record<number, T>,
  before: FormActivityUI[],
  after: FormActivityUI[],
): Record<number, T> => {
  const indexByRef = new Map<FormActivityUI, number>();
  const indexById = new Map<number, number>();
  before.forEach((activity, index) => {
    indexByRef.set(activity, index);
    if (activity.id != null && !indexById.has(activity.id)) {
      indexById.set(activity.id, index);
    }
  });

  const next: Record<number, T> = {};
  after.forEach((activity, index) => {
    const previousIndex =
      indexByRef.get(activity) ??
      (activity.id != null ? indexById.get(activity.id) : undefined);
    if (previousIndex === undefined) return;
    if (previousIndex in record) next[index] = record[previousIndex];
  });
  return next;
};
